/**
 * Photo Wallpaper Crop Guide
 *
 * Calculator + crop modal for photo-wallpaper products.
 * Configuration is passed from Liquid via data-attributes on the custom element.
 * No product handles are hardcoded in JS.
 */

(function () {
  if (window.customElements.get('photo-wallpaper-crop-guide')) return;

  class PhotoWallpaperCropGuide extends HTMLElement {
    connectedCallback() {
      this._formId = this.dataset.formId;
      this._productHandle = this.dataset.productHandle;

      try { this._variants = JSON.parse(this.dataset.variants || '[]'); } catch (_) { this._variants = []; }
      try { this._optionNames = JSON.parse(this.dataset.optionNames || '[]'); } catch (_) { this._optionNames = []; }
      this._firstImage = this.dataset.firstImage || '';

      // State
      this._wallWidth = 0;
      this._wallHeight = 0;
      this._recommendedVariant = null;
      this._cropX = 0;
      this._cropY = 0;
      this._cropConfirmed = false;
      this._assistedActive = false;
      this._calculatorOpened = false;
      this._isDragging = false;
      this._dragStartX = 0;
      this._dragStartY = 0;
      this._dragOffsetX = 0;
      this._dragOffsetY = 0;

      // Preview coordinate state
      this._previewScaleX = 1;
      this._previewScaleY = 1;
      this._previewOffsetX = 0;
      this._previewOffsetY = 0;
      this._standardWidth = 0;
      this._standardHeight = 0;
      this._trimLeft = 0;
      this._trimRight = 0;
      this._trimTop = 0;
      this._trimBottom = 0;

      // Bound handlers
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onWindowResize = this._onWindowResize.bind(this);

      this._init();
    }

    /* ------------------------------------------------------------------ */
    /*  Init                                                               */
    /* ------------------------------------------------------------------ */

    _init() {
      this._form = this._findForm();
      if (!this._form) return;

      // Intercept form submit when assisted flow is active but crop not confirmed
      this._form.addEventListener('submit', (e) => {
        if (this._assistedActive && !this._cropConfirmed) {
          e.preventDefault();
          e.stopPropagation();
          this._openModal();
          return false;
        }

        if (this._assistedActive && this._cropConfirmed) {
          this._event('assisted_add_to_cart', this._eventPayload());
        } else {
          this._event('manual_add_to_cart', { variant_id: this._currentVariantId() });
        }
      }, { capture: true });

      // Listen for manual variant changes
      this._form.addEventListener('variant:change', (e) => {
        const v = e.detail && e.detail.variant;
        if (v && this._recommendedVariant && v.id !== this._recommendedVariant.id && this._assistedActive) {
          if (this._syncRecommendationAfterMaterialChange(v)) return;

          this._cropConfirmed = false;
          this._assistedActive = false;
          this._updateConfirmationUI();
          this._removeFormProperties();
          this._resetMainPriceDisplay();
          this._saveState();
        }
      });

      // Calculator button
      const calc = this.querySelector('.pw-cg__calc');
      if (calc) {
        calc.addEventListener('focusin', () => this._trackCalculatorOpened(), { once: true });
        calc.addEventListener('pointerdown', () => this._trackCalculatorOpened(), { once: true });
      }

      const calcBtn = this.querySelector('.pw-cg__calc-btn');
      if (calcBtn) calcBtn.addEventListener('click', () => this._calculate());

      // Enter key on inputs
      const wInput = this.querySelector('.pw-cg__width');
      const hInput = this.querySelector('.pw-cg__height');
      if (wInput) wInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this._calculate(); } });
      if (hInput) hInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this._calculate(); } });

      // Modal close / confirm
      const closeBtn = this.querySelector('.pw-cg__modal-close');
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeModal());

      const confirmBtn = this.querySelector('.pw-cg__modal-confirm');
      if (confirmBtn) confirmBtn.addEventListener('click', () => this._confirmCrop());

      // Backdrop click
      const modal = this.querySelector('.pw-cg__modal');
      if (modal) modal.addEventListener('click', (e) => { if (e.target === modal) this._closeModal(); });

      // Escape key
      if (modal) modal.addEventListener('cancel', (e) => { e.preventDefault(); this._closeModal(); });

      // Crop frame drag (pointer events handle mouse + touch)
      const frame = this.querySelector('.pw-cg__crop-frame');
      if (frame) frame.addEventListener('pointerdown', (e) => this._onPointerDown(e));
      if (frame) frame.addEventListener('keydown', (e) => this._onCropKeydown(e));

      window.addEventListener('resize', this._onWindowResize);

      // Restore state
      this._applyMaterialDisplayLabels();
      this._restoreState();

      requestAnimationFrame(() => this._updateInitialPriceDisplay());
    }

    _findForm() {
      if (!this._formId) return null;
      return document.getElementById(this._formId) || document.forms[this._formId] || null;
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onWindowResize);
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
    }

    /* ------------------------------------------------------------------ */
    /*  Variant dimension parsing                                          */
    /* ------------------------------------------------------------------ */

    _parseDimsFromText(text) {
      if (!text) return null;
      var m = String(text).match(/(\d+)\s*(?:cm)?\s*[xX\u00d7]\s*(\d+)\s*(?:cm)?/);
      return m ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) } : null;
    }

    _dimsForVariant(variant) {
      if (!variant) return null;

      if (variant.options && this._optionNames) {
        for (var i = 0; i < variant.options.length; i++) {
          var optionName = this._optionNames[i] || '';
          var optionValue = variant.options[i] || '';
          if (/st\u00f8rrelse|size|bredde|h\u00f8jde/i.test(optionName)) {
            var fromNamedOption = this._parseDimsFromText(optionValue);
            if (fromNamedOption) return fromNamedOption;
          }
        }

        for (var j = 0; j < variant.options.length; j++) {
          var fromAnyOption = this._parseDimsFromText(variant.options[j]);
          if (fromAnyOption) return fromAnyOption;
        }
      }

      return this._parseDimsFromText(variant.title);
    }

    /* ------------------------------------------------------------------ */
    /*  Recommendation algorithm                                           */
    /* ------------------------------------------------------------------ */

    _recommend(ww, wh, preferredMaterial) {
      var list = [];
      for (var i = 0; i < this._variants.length; i++) {
        var v = this._variants[i];
        if (!v.available) continue;
        if (preferredMaterial && this._normalizeOptionValue(this._materialForVariant(v)) !== this._normalizeOptionValue(preferredMaterial)) continue;
        var d = this._dimsForVariant(v);
        if (!d) continue;
        if (d.w < ww || d.h < wh) continue; // too small
        list.push({ v: v, d: d, area: d.w * d.h, price: v.price, wSurplus: d.w - ww, order: i });
      }
      if (!list.length) return null;
      list.sort(function (a, b) {
        if (a.area !== b.area) return a.area - b.area;
        if (a.price !== b.price) return a.price - b.price;
        if (a.wSurplus !== b.wSurplus) return a.wSurplus - b.wSurplus;
        return a.order - b.order;
      });
      return list[0];
    }

    /* ------------------------------------------------------------------ */
    /*  Calculator                                                         */
    /* ------------------------------------------------------------------ */

    _calculate() {
      this._trackCalculatorOpened();

      var wInput = this.querySelector('.pw-cg__width');
      var hInput = this.querySelector('.pw-cg__height');
      var ww = parseInt(wInput && wInput.value, 10);
      var wh = parseInt(hInput && hInput.value, 10);

      if (!ww || !wh || ww <= 0 || wh <= 0) {
        this._assistedActive = false;
        this._cropConfirmed = false;
        this._recommendedVariant = null;
        this._showMsg('error', 'Indtast venligst gyldige m\u00e5l i cm.');
        this._updateConfirmationUI();
        this._removeFormProperties();
        this._resetMainPriceDisplay();
        this._saveState();
        return;
      }

      this._wallWidth = ww;
      this._wallHeight = wh;
      this._assistedActive = true;
      this._cropConfirmed = false;

      this._event('dimensions_entered', { wall_width_cm: ww, wall_height_cm: wh });

      var selectedMaterial = this._selectedMaterial();
      var res = this._recommend(ww, wh, selectedMaterial);
      if (!res) {
        this._showMsg('no-fit');
        this._event('no_fit', { wall_width_cm: ww, wall_height_cm: wh, no_fit_reason: 'wall_larger_than_available_variants' });
        this._assistedActive = false;
        this._updateConfirmationUI();
        this._removeFormProperties();
        this._resetMainPriceDisplay();
        this._saveState();
        return;
      }

      this._recommendedVariant = res.v;

      // Default crop: centred
      this._cropX = Math.round((res.d.w - ww) / 2);
      this._cropY = Math.round((res.d.h - wh) / 2);

      this._saveState();
      this._selectVariantOptions(res.v);
      this._setFormVariant(res.v.id);

      this._showMsg('recommendation', res);
      this._event('recommendation', {
        variant_id: res.v.id,
        standard_width_cm: res.d.w,
        standard_height_cm: res.d.h,
        wall_width_cm: ww,
        wall_height_cm: wh,
      });

      this._updateConfirmationUI();
      this._saveState();
    }

    /* ------------------------------------------------------------------ */
    /*  UI messages                                                        */
    /* ------------------------------------------------------------------ */

    _showMsg(type, data) {
      var el = this.querySelector('.pw-cg__result');
      if (!el) return;
      el.classList.add('pw-cg__result--visible');

      if (type === 'error') {
        el.innerHTML = '<p class="pw-cg__error">' + data + '</p>';
        return;
      }

      if (type === 'no-fit') {
        el.innerHTML =
          '<div class="pw-cg__card pw-cg__card--warn">' +
          '<p class="pw-cg__card-title">Denne fototapet f\u00e5s desv\u00e6rre ikke stor nok til dine m\u00e5l.</p>' +
          '<p>Du kan <a href="/pages/kontakt" class="pw-cg__link">kontakte os</a> for r\u00e5dgivning, eller v\u00e6lge en standardst\u00f8rrelse manuelt.</p>' +
          '</div>';
        return;
      }

      if (type === 'recommendation' && data) {
        var variantPrice = this._fmtPrice(data.v.price);
        var pricePerM2 = this._computeVariantPricePerM2(data.v);
        var pricePerM2Display = pricePerM2 ? this._fmtPricePerM2(pricePerM2) : '';
        var pricePerM2Inline = pricePerM2Display ? ' (' + pricePerM2Display + ')' : '';
        var size = data.d.w + ' \u00d7 ' + data.d.h + ' cm';
        var wallSize = this._wallWidth + ' \u00d7 ' + this._wallHeight + ' cm';
        var material = this._displayMaterial(this._materialForVariant(data.v));
        el.innerHTML =
          '<div class="pw-cg__card">' +
          '<p class="pw-cg__card-kicker">Vi anbefaler</p>' +
          '<div class="pw-cg__recommendation">' +
          '<div><span>Din v\u00e6g</span><strong>' + wallSize + '</strong></div>' +
          '<div><span>Standardst\u00f8rrelse</span><strong>' + size + '</strong></div>' +
          '</div>' +
          (material ? '<p class="pw-cg__card-material">Materiale: <strong>' + material + '</strong></p>' : '') +
          '<p class="pw-cg__card-price">Totalpris: ' + variantPrice + pricePerM2Inline + '</p>' +
          '<p class="pw-cg__card-next">St\u00f8rrelsen er valgt automatisk nedenfor. V\u00e6lg materiale, og bekr\u00e6ft besk\u00e6ringen f\u00f8r du l\u00e6gger i kurv.</p>' +
          '<p class="pw-cg__card-info">Tapetet leveres lidt st\u00f8rre end din v\u00e6g. Overskydende tapet sk\u00e6res v\u00e6k ved ops\u00e6tning.</p>' +
          '<button type="button" class="pw-cg__open-crop-btn button w-full">Se og bekr\u00e6ft besk\u00e6ring</button>' +
          '</div>';
        var btn = el.querySelector('.pw-cg__open-crop-btn');
        if (btn) btn.addEventListener('click', () => this._openModal());

        this._updateMainPriceDisplay(data.v);
      }
    }

    _fmtPrice(cents) {
      var amount = (cents / 100).toFixed(2);
      if (amount.endsWith('.00')) amount = amount.slice(0, -3);
      amount = amount.replace('.', ',');
      return amount + ' kr.';
    }

    _fmtPricePerM2(pricePerM2Cents) {
      var amount = (pricePerM2Cents / 100).toFixed(0);
      return amount + ' kr./m\u00b2';
    }

    _computeVariantPricePerM2(variant) {
      var dims = this._dimsForVariant(variant);
      if (!dims) return null;
      var areaM2 = (dims.w * dims.h) / 10000;
      return variant.price / areaM2;
    }

    _computeLowestPricePerM2(material) {
      var best = null;
      for (var i = 0; i < this._variants.length; i++) {
        var v = this._variants[i];
        if (!v.available) continue;
        if (material) {
          var mat = this._materialForVariant(v);
          if (!mat || this._normalizeOptionValue(mat) !== this._normalizeOptionValue(material)) continue;
        }
        var dims = this._dimsForVariant(v);
        if (!dims) continue;
        var m2 = (dims.w * dims.h) / 10000;
        var ppm2 = v.price / m2;
        if (!best || ppm2 < best.ppm2) best = { ppm2: ppm2, variant: v, dims: dims };
      }
      return best;
    }

    _updateMainPriceDisplay(variant) {
      var container = this.closest('.product-info') || this.parentElement;
      if (!container) return;

      var salePrice = container.querySelector('sale-price');
      if (!salePrice) return;

      if (!salePrice.dataset.pwCgOriginalHtml) {
        salePrice.dataset.pwCgOriginalHtml = salePrice.innerHTML;
      }

      var srOnly = salePrice.querySelector('.sr-only');
      var srHtml = srOnly ? srOnly.outerHTML : '';
      var pricePerM2 = this._computeVariantPricePerM2(variant);
      var pricePerM2Display = pricePerM2 ? ' (' + this._fmtPricePerM2(pricePerM2) + ')' : '';
      salePrice.innerHTML = srHtml + 'Totalpris ' + this._fmtPrice(variant.price) + pricePerM2Display;
    }

    _updateInitialPriceDisplay() {
      if (this._assistedActive) return;

      var material = this._selectedMaterial();
      if (!material) material = 'Premium Papirstapet';

      var best = this._computeLowestPricePerM2(material);
      if (!best) return;

      this._lowestPricePerM2 = Math.round(best.ppm2 / 100);

      var container = this.closest('.product-info') || this.parentElement;
      if (!container) return;

      var salePrice = container.querySelector('sale-price');
      if (!salePrice) return;

      if (!salePrice.dataset.pwCgOriginalHtml) {
        salePrice.dataset.pwCgOriginalHtml = salePrice.innerHTML;
      }

      var srOnly = salePrice.querySelector('.sr-only');
      var srHtml = srOnly ? srOnly.outerHTML : '';
      salePrice.innerHTML = srHtml + 'Fra ' + this._lowestPricePerM2 + ' kr./m\u00b2';
    }

    _resetMainPriceDisplay() {
      this._updateInitialPriceDisplay();
    }

    /* ------------------------------------------------------------------ */
    /*  Form variant                                                       */
    /* ------------------------------------------------------------------ */

    _setFormVariant(id) {
      if (!this._form) return;
      var inp = this._form.querySelector('[name="id"]');
      if (inp) {
        inp.value = id;
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _selectVariantOptions(variant) {
      if (!variant || !variant.options) return;

      var picker = document.querySelector('variant-picker[form-id="' + this._formId + '"]');
      if (!picker) return;

      for (var i = 0; i < variant.options.length; i++) {
        var optionValue = this._normalizeOptionValue(variant.options[i]);
        var inputs = picker.querySelectorAll('input[data-option-position="' + (i + 1) + '"][form="' + this._formId + '"]');

        for (var j = 0; j < inputs.length; j++) {
          var input = inputs[j];
          var label = input.closest('label') || (input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null);
          var labelValue = this._normalizeOptionValue(input.dataset.optionValueName || (label ? label.textContent : ''));

          if (labelValue === optionValue) {
            if (!input.checked) {
              input.checked = true;
            }
            break;
          }
        }
      }

      this._updateManualSizeSummary(variant);
    }

    _updateManualSizeSummary(variant) {
      if (!variant || !variant.options || !this._optionNames) return;

      for (var i = 0; i < this._optionNames.length; i++) {
        if (!/st\u00f8rrelse/i.test(this._optionNames[i])) continue;

        var picker = document.querySelector('variant-picker[form-id="' + this._formId + '"]');
        var manualSize = picker && picker.querySelector('details.pw-cg-manual-size');
        var selectedText = manualSize && manualSize.querySelector('summary span:last-child');
        if (selectedText) selectedText.textContent = variant.options[i] || '';

        var selectedValue = manualSize && manualSize.querySelector('[id$="-selected-value"]');
        if (selectedValue) selectedValue.textContent = variant.options[i] || '';
        break;
      }
    }

    _normalizeOptionValue(value) {
      return String(value || '').toLowerCase().replace(/\s+/g, '').replace(/\u00d7/g, 'x');
    }

    _currentVariantId() {
      if (!this._form) return null;
      var inp = this._form.querySelector('[name="id"]');
      return inp && inp.value ? parseInt(inp.value, 10) : null;
    }

    _selectedMaterial() {
      var picker = document.querySelector('variant-picker[form-id="' + this._formId + '"]');
      if (!picker) return this._materialForVariant(this._variants.find((v) => v.id === this._currentVariantId()));

      for (var i = 0; i < this._optionNames.length; i++) {
        if (!/materiale/i.test(this._optionNames[i])) continue;
        var checked = picker.querySelector('input[data-option-position="' + (i + 1) + '"][form="' + this._formId + '"]:checked');
        if (!checked) break;
        var label = checked.closest('label') || (checked.nextElementSibling && checked.nextElementSibling.tagName === 'LABEL' ? checked.nextElementSibling : null);
        return checked.dataset.optionValueName || (label ? label.textContent.trim() : checked.value);
      }

      return this._materialForVariant(this._variants.find((v) => v.id === this._currentVariantId()));
    }

    _applyMaterialDisplayLabels() {
      var picker = document.querySelector('variant-picker[form-id="' + this._formId + '"]');
      if (!picker || !this._optionNames) return;

      for (var i = 0; i < this._optionNames.length; i++) {
        if (!/materiale/i.test(this._optionNames[i])) continue;

        var inputs = picker.querySelectorAll('input[data-option-position="' + (i + 1) + '"][form="' + this._formId + '"]');
        inputs.forEach((input) => {
          var label = input.closest('label') || (input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL' ? input.nextElementSibling : null);
          if (!label) return;

          var originalMaterial = input.dataset.optionValueName || label.textContent.trim();
          if (!originalMaterial) return;

          input.dataset.optionValueName = originalMaterial;

          var textTarget = label.querySelector('span:last-child') || label;
          textTarget.textContent = this._displayMaterial(originalMaterial);
        });
        break;
      }
    }

    _syncRecommendationAfterMaterialChange(variant) {
      if (!variant || !this._recommendedVariant || !this._wallWidth || !this._wallHeight) return false;

      var oldDims = this._dimsForVariant(this._recommendedVariant);
      var newDims = this._dimsForVariant(variant);
      if (!oldDims || !newDims) return false;

      // Material changes produce a new variant ID while keeping the recommended
      // wall-covering size. Treat that as assisted-flow continuity, not manual size selection.
      if (oldDims.w !== newDims.w || oldDims.h !== newDims.h) return false;
      if (newDims.w < this._wallWidth || newDims.h < this._wallHeight) return false;

      this._recommendedVariant = variant;
      this._cropConfirmed = false;
      this._removeFormProperties();
      this._updateManualSizeSummary(variant);
      this._showMsg('recommendation', { v: variant, d: newDims });
      this._updateConfirmationUI();
      this._saveState();
      this._event('material_changed_after_recommendation', this._eventPayload());
      return true;
    }

    _materialForVariant(variant) {
      if (!variant || !variant.options || !this._optionNames) return null;
      for (var i = 0; i < this._optionNames.length; i++) {
        if (/materiale/i.test(this._optionNames[i])) return variant.options[i] || null;
      }
      return null;
    }

    _displayMaterial(material) {
      if (!material) return '';
      return /vinyl/i.test(material) ? 'Ekstra slidst\u00e6rkt fototapet' : 'Standard fototapet';
    }

    /* ------------------------------------------------------------------ */
    /*  Form line-item properties                                          */
    /* ------------------------------------------------------------------ */

    _updateFormProperties() {
      this._removeFormProperties();
      if (!this._assistedActive || !this._cropConfirmed || !this._form) return;
      if (!this._recommendedVariant) return;
      var d = this._dimsForVariant(this._recommendedVariant);
      if (!d) return;

      var props = [
        ['V\u00e6ggens bredde', this._wallWidth + ' cm'],
        ['V\u00e6ggens h\u00f8jde', this._wallHeight + ' cm'],
        ['Anbefalet standardst\u00f8rrelse', d.w + ' x ' + d.h + ' cm'],
        ['Besk\u00e6ring venstre', this._trimLeft + ' cm'],
        ['Besk\u00e6ring h\u00f8jre', this._trimRight + ' cm'],
        ['Besk\u00e6ring top', this._trimTop + ' cm'],
        ['Besk\u00e6ring bund', this._trimBottom + ' cm'],
        ['Besk\u00e6ringsguide', 'Ja'],
      ];
      var priv = [
        ['_crop_x', this._cropX],
        ['_crop_y', this._cropY],
        ['_wall_width_cm', this._wallWidth],
        ['_wall_height_cm', this._wallHeight],
        ['_standard_width_cm', d.w],
        ['_standard_height_cm', d.h],
        ['_guide_version', '1.0'],
      ];

      var form = this._form;
      props.concat(priv).forEach(function (pair) {
        var inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = 'properties[' + pair[0] + ']';
        inp.value = pair[1];
        inp.classList.add('pw-cg__prop');
        form.appendChild(inp);
      });
    }

    _removeFormProperties() {
      if (!this._form) return;
      this._form.querySelectorAll('.pw-cg__prop').forEach(function (el) { el.remove(); });
    }

    /* ------------------------------------------------------------------ */
    /*  Crop modal                                                         */
    /* ------------------------------------------------------------------ */

    _openModal() {
      if (!this._recommendedVariant) return;
      var d = this._dimsForVariant(this._recommendedVariant);
      if (!d) return;

      this._standardWidth = d.w;
      this._standardHeight = d.h;

      var modal = this.querySelector('.pw-cg__modal');
      var preview = this.querySelector('.pw-cg__preview');
      if (!modal || !preview) return;

      preview.style.setProperty('--pw-cg-preview-ratio', d.w + ' / ' + d.h);

      // Set image
      var img = this.querySelector('.pw-cg__preview-img');
      if (img && !img.src && this._firstImage) {
        img.src = this._firstImage;
        img.alt = 'Forh\u00e5ndsvisning af fototapet';
      }

      // Wait for layout then position
      requestAnimationFrame(() => this._layoutPreview());

      modal.showModal();
      this._event('crop_opened', {
        variant_id: this._recommendedVariant.id,
        standard_width_cm: d.w,
        standard_height_cm: d.h,
      });
    }

    _closeModal() {
      var modal = this.querySelector('.pw-cg__modal');
      if (modal) modal.close();
    }

    _layoutPreview() {
      var container = this.querySelector('.pw-cg__preview');
      var img = this.querySelector('.pw-cg__preview-img');
      if (!container || !img) return;

      var cw = container.clientWidth;
      var ch = container.clientHeight;
      if (!cw || !ch) return;

      // Standard wallpaper aspect ratio
      var sRatio = this._standardWidth / this._standardHeight;
      var cRatio = cw / ch;
      var dw, dh;

      if (sRatio > cRatio) {
        dw = cw;
        dh = cw / sRatio;
      } else {
        dh = ch;
        dw = ch * sRatio;
      }

      img.style.width = dw + 'px';
      img.style.height = dh + 'px';
      img.style.left = ((cw - dw) / 2) + 'px';
      img.style.top = ((ch - dh) / 2) + 'px';

      this._previewScaleX = dw / this._standardWidth;
      this._previewScaleY = dh / this._standardHeight;
      this._previewOffsetX = (cw - dw) / 2;
      this._previewOffsetY = (ch - dh) / 2;

      this._positionCropFrame();
      this._updateTrimLabels();
    }

    _positionCropFrame() {
      var frame = this.querySelector('.pw-cg__crop-frame');
      if (!frame) return;

      var fw = this._wallWidth * this._previewScaleX;
      var fh = this._wallHeight * this._previewScaleY;
      var fx = this._cropX * this._previewScaleX + this._previewOffsetX;
      var fy = this._cropY * this._previewScaleY + this._previewOffsetY;

      frame.style.width = fw + 'px';
      frame.style.height = fh + 'px';
      frame.style.left = fx + 'px';
      frame.style.top = fy + 'px';

      // Show drag hint only when there is surplus
      var hint = this.querySelector('.pw-cg__drag-hint');
      if (hint) {
        var canDrag = this._standardWidth > this._wallWidth || this._standardHeight > this._wallHeight;
        hint.hidden = !canDrag;
      }
    }

    _clampCrop() {
      this._cropX = Math.max(0, Math.min(Math.round(this._cropX), Math.max(0, this._standardWidth - this._wallWidth)));
      this._cropY = Math.max(0, Math.min(Math.round(this._cropY), Math.max(0, this._standardHeight - this._wallHeight)));
    }

    _moveCropBy(dx, dy) {
      if (!this._standardWidth || !this._standardHeight) return;
      this._cropX += dx;
      this._cropY += dy;
      this._clampCrop();
      this._positionCropFrame();
      this._updateTrimLabels();
      this._cropConfirmed = false;
      this._updateConfirmationUI();
      this._removeFormProperties();
      this._saveState();
    }

    _updateTrimLabels() {
      this._calculateTrims();

      var labels = this.querySelectorAll('.pw-cg__trim-val');
      if (labels.length >= 4) {
        labels[0].textContent = this._trimLeft + ' cm';
        labels[1].textContent = this._trimRight + ' cm';
        labels[2].textContent = this._trimTop + ' cm';
        labels[3].textContent = this._trimBottom + ' cm';
      }

      this._updateTrimSummary();
    }

    _calculateTrims() {
      this._trimLeft = Math.round(this._cropX);
      this._trimTop = Math.round(this._cropY);
      this._trimRight = Math.round(this._standardWidth - this._wallWidth - this._cropX);
      this._trimBottom = Math.round(this._standardHeight - this._wallHeight - this._cropY);
    }

    _updateTrimSummary() {
      var el = this.querySelector('.pw-cg__trim-summary');
      if (!el) return;

      var parts = [
        this._trimLeft ? this._trimLeft + ' cm fra venstre' : null,
        this._trimRight ? this._trimRight + ' cm fra h\u00f8jre' : null,
        this._trimTop ? this._trimTop + ' cm fra toppen' : null,
        this._trimBottom ? this._trimBottom + ' cm fra bunden' : null,
      ].filter(Boolean);

      if (!parts.length) {
        el.hidden = true;
        el.textContent = '';
        return;
      }

      el.hidden = false;
      el.textContent = 'Du skal sk\u00e6re ' + parts.join(', ') + ' efter ops\u00e6tning.';
    }

    /* ------------------------------------------------------------------ */
    /*  Crop drag                                                          */
    /* ------------------------------------------------------------------ */

    _onPointerDown(e) {
      if (e.button && e.button !== 0) return;
      e.preventDefault();
      this._isDragging = true;
      this._dragStartX = e.clientX;
      this._dragStartY = e.clientY;
      this._dragOffsetX = this._cropX;
      this._dragOffsetY = this._cropY;

      var frame = this.querySelector('.pw-cg__crop-frame');
      if (frame) frame.setPointerCapture(e.pointerId);

      document.addEventListener('pointermove', this._onPointerMove);
      document.addEventListener('pointerup', this._onPointerUp);
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    _onPointerMove(e) {
      if (!this._isDragging) return;
      var dx = e.clientX - this._dragStartX;
      var dy = e.clientY - this._dragStartY;

      var nx = this._dragOffsetX + dx / this._previewScaleX;
      var ny = this._dragOffsetY + dy / this._previewScaleY;

      nx = Math.max(0, Math.min(nx, this._standardWidth - this._wallWidth));
      ny = Math.max(0, Math.min(ny, this._standardHeight - this._wallHeight));

      this._cropX = Math.round(nx);
      this._cropY = Math.round(ny);
      this._clampCrop();

      this._positionCropFrame();
      this._updateTrimLabels();
    }

    _onPointerUp() {
      this._isDragging = false;
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      this._event('crop_adjusted', { crop_x_cm: this._cropX, crop_y_cm: this._cropY });
      this._saveState();
    }

    _onCropKeydown(e) {
      var keyMap = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      var delta = keyMap[e.key];
      if (!delta) return;

      e.preventDefault();
      var step = e.shiftKey ? 10 : 1;
      this._moveCropBy(delta[0] * step, delta[1] * step);
      this._event('crop_adjusted', { crop_x_cm: this._cropX, crop_y_cm: this._cropY, input: 'keyboard' });
    }

    _onWindowResize() {
      var modal = this.querySelector('.pw-cg__modal');
      if (modal && modal.open) requestAnimationFrame(() => this._layoutPreview());
    }

    /* ------------------------------------------------------------------ */
    /*  Crop confirmation                                                  */
    /* ------------------------------------------------------------------ */

    _confirmCrop() {
      this._cropConfirmed = true;
      if (this._recommendedVariant) {
        this._selectVariantOptions(this._recommendedVariant);
        this._setFormVariant(this._recommendedVariant.id);
      }
      this._updateConfirmationUI();
      this._closeModal();
      this._updateFormProperties();
      this._saveState();

      this._event('crop_confirmed', {
        crop_x_cm: this._cropX,
        crop_y_cm: this._cropY,
        trim_left_cm: this._trimLeft,
        trim_right_cm: this._trimRight,
        trim_top_cm: this._trimTop,
        trim_bottom_cm: this._trimBottom,
      });
    }

    _updateConfirmationUI() {
      var confirmed = this._assistedActive && this._cropConfirmed;
      var summary = this.querySelector('.pw-cg__summary');
      var checkbox = this.querySelector('.pw-cg__confirm-cb');

      if (summary) {
        summary.hidden = !confirmed;
        if (confirmed && this._recommendedVariant) {
          var d = this._dimsForVariant(this._recommendedVariant);
          var sizeEl = summary.querySelector('.pw-cg__summary-size');
          if (sizeEl && d) sizeEl.textContent = d.w + ' \u00d7 ' + d.h + ' cm';
        }
      }
      if (checkbox) checkbox.checked = confirmed;

      this._updateBuyButtonState();
    }

    _updateBuyButtonState() {
      if (!this._formId) return;
      var buttons = document.querySelectorAll('button[type="submit"][form="' + this._formId + '"], form#' + this._formId + ' button[type="submit"]');
      var shouldGate = this._assistedActive && !this._cropConfirmed && this._recommendedVariant;

      buttons.forEach((button) => {
        if (!button.dataset.pwCgOriginalText) button.dataset.pwCgOriginalText = button.textContent.trim();
        if (shouldGate) {
          button.classList.add('pw-cg-atc-needs-confirmation');
          button.textContent = 'Kontroll\u00e9r besk\u00e6ring og l\u00e6g i kurv';
        } else if (this._assistedActive && this._cropConfirmed && this._recommendedVariant) {
          button.classList.remove('pw-cg-atc-needs-confirmation');
          button.textContent = 'F\u00f8j anbefalet st\u00f8rrelse til kurv';
        } else {
          button.classList.remove('pw-cg-atc-needs-confirmation');
          if (button.dataset.pwCgOriginalText) button.textContent = button.dataset.pwCgOriginalText;
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Analytics                                                          */
    /* ------------------------------------------------------------------ */

    _event(name, data) {
      window.dataLayer = window.dataLayer || [];
      var payload = Object.assign({ product_handle: this._productHandle }, data || {});
      window.dataLayer.push({ event: 'crop_guide_' + name, crop_guide: payload });
    }

    _trackCalculatorOpened() {
      if (this._calculatorOpened) return;
      this._calculatorOpened = true;
      this._event('calculator_opened', { variant_id: this._currentVariantId() });
    }

    _eventPayload() {
      var variant = this._recommendedVariant;
      var dims = variant ? this._dimsForVariant(variant) : null;
      return {
        variant_id: variant ? variant.id : this._currentVariantId(),
        material: this._materialForVariant(variant),
        standard_width_cm: dims ? dims.w : null,
        standard_height_cm: dims ? dims.h : null,
        wall_width_cm: this._wallWidth || null,
        wall_height_cm: this._wallHeight || null,
        trim_left_cm: this._trimLeft,
        trim_right_cm: this._trimRight,
        trim_top_cm: this._trimTop,
        trim_bottom_cm: this._trimBottom,
      };
    }

    /* ------------------------------------------------------------------ */
    /*  State persistence (sessionStorage)                                 */
    /* ------------------------------------------------------------------ */

    _storageKey() { return 'pw-cg-' + this._productHandle; }

    _saveState() {
      try {
        sessionStorage.setItem(this._storageKey(), JSON.stringify({
          ww: this._wallWidth,
          wh: this._wallHeight,
          vid: this._recommendedVariant ? this._recommendedVariant.id : null,
          cx: this._cropX,
          cy: this._cropY,
          cc: this._cropConfirmed,
          aa: this._assistedActive,
        }));
      } catch (_) { /* private mode etc. */ }
    }

    _restoreState() {
      try {
        var raw = sessionStorage.getItem(this._storageKey());
        if (!raw) return;
        var s = JSON.parse(raw);

        var wInput = this.querySelector('.pw-cg__width');
        var hInput = this.querySelector('.pw-cg__height');
        if (wInput && s.ww) wInput.value = s.ww;
        if (hInput && s.wh) hInput.value = s.wh;

        if (!s.aa || !s.ww || !s.wh) return;
        this._wallWidth = s.ww;
        this._wallHeight = s.wh;
        this._assistedActive = true;

        var storedVariant = this._variants.find((v) => v.id === Number(s.vid));
        var storedMaterial = this._materialForVariant(storedVariant);
        var res = this._recommend(s.ww, s.wh, storedMaterial || this._selectedMaterial());
        if (res) {
          this._recommendedVariant = res.v;
          this._cropX = Number.isFinite(Number(s.cx)) ? Number(s.cx) : Math.round((res.d.w - s.ww) / 2);
          this._cropY = Number.isFinite(Number(s.cy)) ? Number(s.cy) : Math.round((res.d.h - s.wh) / 2);
          this._showMsg('recommendation', res);
          this._selectVariantOptions(res.v);
          this._setFormVariant(s.vid || res.v.id);
          this._cropConfirmed = !!s.cc;
          this._standardWidth = res.d.w;
          this._standardHeight = res.d.h;
          this._calculateTrims();
          this._updateConfirmationUI();
          if (this._cropConfirmed) this._updateFormProperties();
        }
      } catch (_) { /* ignore */ }
    }
  }

  window.customElements.define('photo-wallpaper-crop-guide', PhotoWallpaperCropGuide);
})();
